import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { auth } from '../core/firebaseConfig';
import { hasRole, isAdminRole } from '../utils/roles';
import { useOwnRestaurant } from './useOwnRestaurant';

export type AdminEventType = 'DISCOUNT_BUDGET_EXHAUSTED' | 'ORDER_DELIVERY_PROBLEM';

/* Backend wysyla wylacznie identyfikatory (topic nie ma autoryzacji SUBSCRIBE),
   wiec handler sam dociaga szczegoly przez REST z kontrola dostepu. */
export interface AdminEvent {
  type: AdminEventType;
  locationId: number;
  entityId: number;
}

export interface AdminAlert {
  id: string;
  messageKey: string;
  params?: Record<string, unknown>;
  link?: string;
}

export type AdminEventHandler = (event: AdminEvent) => Promise<AdminAlert | null>;

/* Handlery rejestruja moduly, ktore wysylaja dany typ zdarzenia (np. rabaty).
   Nieznany typ jest ignorowany — starszy panel nie wywali sie na nowym zdarzeniu. */
export const adminEventHandlers: Partial<Record<AdminEventType, AdminEventHandler>> = {};

/**
 * Subskrypcja zdarzen lokalu dla RESTAURANT_OWNER / VENDOR. Administratorzy nie
 * dostaja tych alertow (decyzja w SPEC-admin-notifications), wiec dla nich hook
 * nic nie robi.
 */
export const useAdminEvents = (onAlert: (alert: AdminAlert) => void) => {
  const isRecipient = hasRole('RESTAURANT_OWNER', 'VENDOR') && !isAdminRole();
  const { restaurant } = useOwnRestaurant();
  const locationId = isRecipient ? restaurant?.id : undefined;
  const onAlertRef = useRef(onAlert);
  onAlertRef.current = onAlert;

  useEffect(() => {
    if (locationId === undefined) {
      return;
    }

    const topic = `/topic/admin-events/${locationId}`;

    const client = new Client({
      webSocketFactory: () => new SockJS(`${process.env.REACT_APP_API_URL}/ws`),
      beforeConnect: async () => {
        await auth.authStateReady();
        const token = await auth.currentUser?.getIdToken();
        client.connectHeaders = { Authorization: `Bearer ${token}` };
      },
      onConnect: () => {
        client.subscribe(topic, async (message) => {
          try {
            const event = JSON.parse(message.body) as AdminEvent;
            const handler = adminEventHandlers[event.type];

            if (!handler) {
              return;
            }

            const alert = await handler(event);
            if (alert) {
              onAlertRef.current(alert);
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        });
      },
      onStompError: (frame) => {
        Sentry.captureException(
          new Error(`Admin events STOMP error: ${frame.headers['message']}`),
          {
            extra: { body: frame.body },
          },
        );
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.activate();

    return () => {
      client.deactivate();
    };
  }, [locationId]);
};
